//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { StatBubble } from 'unison';

import { ConnectPanelActions } from '@catalog-ingestion/actions/connectPanelActions';
import { GoogleCatalogIngestionModalActions } from '@catalog-ingestion/actions/googleCatalogIngestionModalActions';
import { ThresholdFailedConfirmationModal } from '@catalog-ingestion/components/modals/threshold-failed-confirmation-modal/ThresholdFailedConfirmationModal';
import EmptyProductCatalogError from '@catalog-ingestion/components/review-panel/EmptyProductCatalogError';
import { ExpandingDataTables } from '@catalog-ingestion/components/review-panel/ExpandingDataTables';
import ThresholdFailedDescription from '@catalog-ingestion/components/review-panel/ThresholdFailedDescription';
import ThresholdPassedDescription from '@catalog-ingestion/components/review-panel/ThresholdPassedDescription';
import {
  IMAGE_HEIGHT,
  IMAGE_WIDTH,
} from '@catalog-ingestion/constants/reviewPanelConstants';
import { calculateTruncRowPercentageLabel } from '@catalog-ingestion/utils/calculateRowPercentageLabel';
import { SecondaryButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import {
  BodyText,
  CaptionSecondaryText,
  SubsectionText,
} from '@components/atoms/text/Text';
import { Mixins } from '@theme/mixins';
import { commaSeparatedNumber, readablePrice } from '@utils/readableNumber';

const SuccessRows = ({
  successfulRows,
  processedRows,
}: {
  successfulRows: number;
  processedRows: number;
}) => (
  <StatBubble
    tooltip={`${commaSeparatedNumber(successfulRows)} product${successfulRows === 1 ? ' is' : 's are'} error free`}
    value={calculateTruncRowPercentageLabel(successfulRows, processedRows)}
    statLabel='Products Passed'
  />
);

const FailRows = ({
  failedRows,
  processedRows,
}: {
  failedRows: number;
  processedRows: number;
}) => (
  <StyledStatBubbleFailed
    tooltip={`${commaSeparatedNumber(failedRows)} product${failedRows === 1 ? ' contains' : 's contain'} one or more errors`}
    value={calculateTruncRowPercentageLabel(failedRows, processedRows)}
    statLabel='Products Failed'
  />
);

export const ReviewPanel = () => {
  const dispatch = useDispatch();

  const {
    isThresholdPassed,
    processedRows,
    failedRows,
    successfulRows,
    validSample,
    fullErrorData,
    expandingErrorData,
  } = useSelector(
    (state) => state.catalogIngestionReducers.googleCatalogIngestionModal,
  );

  const { isValidating } = useSelector(
    (state) => state.catalogIngestionReducers.connectPanel,
  );

  const onUploadAgain = () => {
    const {
      resetGoogleCatalogIngestionState,
      openGoogleCatalogIngestionModal,
    } = GoogleCatalogIngestionModalActions;

    if (isValidating) {
      dispatch(ConnectPanelActions.toggleIsValidating());
    }

    dispatch(resetGoogleCatalogIngestionState());
    dispatch(openGoogleCatalogIngestionModal());
  };

  if (validSample === null || fullErrorData === null) {
    return (
      <ContentContainer className='cu-text-subtitle cu-mt-6'>
        Loading your catalog results...
      </ContentContainer>
    );
  } else {
    // only use maximum of the first 4 rows in the sample preview
    const validRows = validSample.rows.slice(0, 4);

    // build the downloadable csv error file
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      fullErrorData.map((row) => row.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);

    return (
      <>
        {processedRows ? (
          <>
            <ThresholdFailedConfirmationModal />
            <Sheet>
              {isThresholdPassed ? (
                <ThresholdPassedDescription />
              ) : (
                <ThresholdFailedDescription />
              )}
              <SubHeaderWrapper>
                <OverviewTitleText>Product Catalog Overview</OverviewTitleText>
                <SecondaryButton onClick={onUploadAgain}>
                  <CenteredContainer>
                    <PanelIcons name='upload' />
                    Upload Catalog Again
                  </CenteredContainer>
                </SecondaryButton>
              </SubHeaderWrapper>
              <StatBubbles className='cu-stat-bubble-group-3'>
                <StyledStatBubbleProcessed
                  value={commaSeparatedNumber(processedRows)}
                  statLabel='Total Products'
                />
                {isThresholdPassed ? (
                  <>
                    <SuccessRows
                      successfulRows={successfulRows}
                      processedRows={processedRows}
                    />
                    <FailRows
                      failedRows={failedRows}
                      processedRows={processedRows}
                    />
                  </>
                ) : (
                  <>
                    <FailRows
                      failedRows={failedRows}
                      processedRows={processedRows}
                    />
                    <SuccessRows
                      successfulRows={successfulRows}
                      processedRows={processedRows}
                    />
                  </>
                )}
              </StatBubbles>
              <ExpandingDataTables
                expandingErrorData={expandingErrorData}
                encodedUri={encodedUri}
                productsFailed={failedRows}
              />
              {validRows.length > 0 && (
                <>
                  <PreviewTitleText>Preview Product Sample</PreviewTitleText>
                  <PreviewDescriptionText>
                    This is a preview of your products. Ensure images, catagory,
                    prices, and product titles are displaying to your
                    preference.
                  </PreviewDescriptionText>
                  <ProductPreviewSection>
                    {validRows.map(
                      ({ imageLink, price, category, title }, index) => {
                        return (
                          <ProductPreview key={index}>
                            <ProductImageContainer>
                              <ProductImage src={imageLink} alt={title} />
                            </ProductImageContainer>
                            {category && (
                              <CaptionSecondaryText>
                                {category}
                              </CaptionSecondaryText>
                            )}
                            <ProductTitleText>{title}</ProductTitleText>
                            {price && (
                              <div className='cu-text-body-2'>
                                {readablePrice(
                                  price.price,
                                  false,
                                  price.currencyCode,
                                )}
                              </div>
                            )}
                          </ProductPreview>
                        );
                      },
                    )}
                  </ProductPreviewSection>
                </>
              )}
            </Sheet>
          </>
        ) : (
          <EmptyProductCatalogError />
        )}
      </>
    );
  }
};

const Sheet = styled.div`
  ${Mixins.Sheet.Low}
  flex-direction: column;
  padding: ${({ theme }) => theme.spacings[4]};
`;

const StatBubbles = styled.div`
  padding-bottom: ${({ theme }) => theme.spacings[4]};
`;

const ContentContainer = styled.div`
  display: flex;
  justify-content: center;
  overflow-y: scroll;
`;

const StyledStatBubbleProcessed = styled(StatBubble)(
  ({ theme }) => `
  && {
    .cu-stat-bubble-content-container {
      border-top: ${theme.borderWidths[3]} solid ${theme.colors.gray5};
    }
  }
`,
);

const StyledStatBubbleFailed = styled(StatBubble)(
  ({ theme }) => `
  && {
    .cu-stat-bubble-content-container {
      border-top: ${theme.borderWidths[3]} solid ${theme.colors.aliases.error};
    }
  }
`,
);

const SubHeaderWrapper = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacings[2]};
  justify-content: space-between;
`;

const PanelIcons = styled(Icon)(
  ({ theme }) => `
  color: ${theme.colors.aliases.primary};
  margin-right: ${theme.spacings[2]};

  :hover {
    color: ${theme.colors.white};
  }
`,
);

const ProductPreviewSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
`;

const ProductPreview = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacings[3]};
  max-width: 175px;
`;

const ProductImage = styled.img`
  max-width: ${IMAGE_WIDTH};
  max-height: ${IMAGE_HEIGHT};
`;

const ProductImageContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacings[2]};
  max-width: ${IMAGE_WIDTH};
  max-height: ${IMAGE_HEIGHT};
`;

const CenteredContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OverviewTitleText = styled(SubsectionText)(
  ({ theme }) => `
  padding-top: ${theme.spacings[1]};
  padding-bottom: ${theme.spacings[1]};
`,
);

const PreviewTitleText = styled(SubsectionText)(
  ({ theme }) => `
  margin-top: ${theme.spacings[4]};
`,
);

const PreviewDescriptionText = styled(BodyText)(
  ({ theme }) => `
  padding-top: ${theme.spacings[1]};
  padding-bottom: ${theme.spacings[3]};
`,
);

const ProductTitleText = styled(BodyText)(
  ({ theme }) => `
  padding-top: ${theme.spacings[1]};
  padding-bottom: ${theme.spacings[1]};
`,
);
